import React, { useEffect, useState, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { numberID, formatDateTime } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, ClipboardCheck, Check, X, Eye, Ban } from "lucide-react";

export default function StockOpname() {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const [wh, setWh] = useState([]);
  const [open, setOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const load = () => { setRows(null); api.get("/inventory/opnames").then((r) => setRows(r.data)); };
  useEffect(() => { api.get("/inventory/warehouses").then((r) => setWh(r.data)); load(); }, []);
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId) { setDetailId(openId); setSearchParams({}, { replace: true }); }
  }, [searchParams, setSearchParams]);
  const whName = (id) => wh.find((w) => w.id === id)?.name || id;
  const approve = async (o) => {
    if (!window.confirm(`Setujui opname ${o.opname_no}? Selisih akan membuat penyesuaian stok.`)) return;
    try { await api.patch(`/inventory/opnames/${o.id}/approve`); toast.success("Opname disetujui"); load(); } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div>
      <PageHeader title="Stock Opname" subtitle="Audit stok fisik. Selisih yang disetujui otomatis menghasilkan penyesuaian." testId="opname-header">
        {can("inventory.opname") && <Button onClick={() => setOpen(true)} data-testid="create-opname-btn" className="bg-indigo-600 hover:bg-indigo-500"><Plus className="mr-1.5 h-4 w-4" /> Buat Opname</Button>}
      </PageHeader>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
        {!rows ? <Loading /> : rows.length === 0 ? <EmptyState title="Belum ada opname" icon={ClipboardCheck} /> : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>No</TableHead><TableHead>Gudang</TableHead><TableHead className="text-right">Item</TableHead><TableHead>Dibuat</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Aksi</TableHead></TableRow></TableHeader>
              <TableBody>
                {rows.map((o) => (
                  <TableRow key={o.id} data-testid={`opname-row-${o.opname_no}`}>
                    <TableCell className="font-mono text-xs"><button className="text-indigo-600 hover:underline" onClick={() => setDetailId(o.id)} data-testid={`opname-link-${o.opname_no}`}>{o.opname_no}</button></TableCell><TableCell>{whName(o.warehouse_id)}</TableCell>
                    <TableCell className="text-right">{o.items.length}</TableCell><TableCell className="text-xs text-slate-500">{formatDateTime(o.created_at)}</TableCell>
                    <TableCell><StatusBadge status={o.status} /></TableCell>
                    <TableCell className="text-right space-x-1 whitespace-nowrap">
                      <Button size="sm" variant="outline" onClick={() => setDetailId(o.id)} data-testid={`detail-opname-${o.opname_no}`}><Eye className="mr-1 h-3.5 w-3.5" /> Detail</Button>
                      {can("inventory.opname") && o.status === "Draft" && <Button size="sm" variant="outline" onClick={() => approve(o)} data-testid={`approve-opname-${o.opname_no}`}><Check className="mr-1 h-3.5 w-3.5" /> Setujui</Button>}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
      {open && <OpnameDialog wh={wh} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />}
      {detailId && <OpnameDetailDialog id={detailId} whName={whName} onClose={() => setDetailId(null)} onChanged={load} />}
    </div>
  );
}

function OpnameDialog({ wh, onClose, onSaved }) {
  const [warehouseId, setWarehouseId] = useState(wh.find((w) => w.type === "PUSAT")?.id || "");
  const [basis, setBasis] = useState("ON_HAND_TOTAL");
  const [balances, setBalances] = useState([]);
  const [physical, setPhysical] = useState({});
  const [saving, setSaving] = useState(false);
  const free = basis === "AVAILABLE_FREE";
  useEffect(() => {
    if (!warehouseId) return;
    const w = wh.find((x) => x.id === warehouseId);
    const refType = w?.type === "PUSAT" ? "variant" : "item";
    setPhysical({});
    api.get("/inventory/balances", { params: { warehouse_id: warehouseId, ref_type: refType } }).then((r) => setBalances(r.data));
  }, [warehouseId]);
  // Default count = on_hand (TOTAL) or available (FREE). Projection uses the uniform posting rule.
  const defFor = (b) => (free ? (b.on_hand - (b.reserved || 0)) : b.on_hand);
  const save = async () => {
    const items = balances.map((b) => ({
      ref_type: b.ref_type, ref_id: b.ref_id, system_qty: b.on_hand,
      physical_qty: Number(physical[b.ref_id] ?? defFor(b)),
    }));
    if (items.length === 0) return toast.error("Tidak ada stok untuk diopname");
    setSaving(true);
    try { await api.post("/inventory/opnames", { warehouse_id: warehouseId, count_basis: basis, items }); toast.success("Opname dibuat"); onSaved(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Buat Stock Opname</DialogTitle><DialogDescription>Pilih dasar penghitungan, lalu masukkan hasil hitung fisik. Reserved tidak diubah oleh opname.</DialogDescription></DialogHeader>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className="text-xs text-slate-500">Gudang</Label>
            <Select value={warehouseId} onValueChange={setWarehouseId}><SelectTrigger className="mt-1" data-testid="opname-wh"><SelectValue /></SelectTrigger><SelectContent>{wh.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}</SelectContent></Select>
          </div>
          <div><Label className="text-xs text-slate-500">Dasar Penghitungan</Label>
            <div className="mt-1 grid grid-cols-2 gap-1">
              <Button type="button" variant={free ? "outline" : "default"} className={!free ? "bg-indigo-600 hover:bg-indigo-500" : ""} onClick={() => { setBasis("ON_HAND_TOTAL"); setPhysical({}); }} data-testid="opname-basis-total">Fisik Total Gudang</Button>
              <Button type="button" variant={free ? "default" : "outline"} className={free ? "bg-indigo-600 hover:bg-indigo-500" : ""} onClick={() => { setBasis("AVAILABLE_FREE"); setPhysical({}); }} data-testid="opname-basis-free">Fisik Stok Bebas</Button>
            </div>
          </div>
        </div>
        <div className="rounded-lg border border-indigo-100 bg-indigo-50/60 p-3 text-xs text-indigo-900" data-testid="opname-basis-help">
          {free
            ? "Stok Bebas / Siap Jual: hitung HANYA barang yang belum dialokasikan (tidak ter-reserve). Target Available = hasil hitung; On Hand = hasil hitung + Reserved tervalidasi."
            : "Fisik Total Gudang: hitung SELURUH barang fisik, termasuk yang sudah dialokasikan (ter-reserve). Target On Hand = hasil hitung."}
        </div>
        <div className="max-h-80 overflow-y-auto rounded-lg border border-slate-200">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Item</TableHead>
              <TableHead className="text-right">On Hand</TableHead>
              <TableHead className="text-right">Reserved</TableHead>
              <TableHead className="text-right">Available</TableHead>
              <TableHead className="text-right w-28">{free ? "Hitung Bebas" : "Hitung Total"}</TableHead>
              <TableHead className="text-right">Estimasi Setelah Setuju</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {balances.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center text-sm text-slate-400 py-4">Belum ada stok</TableCell></TableRow> :
                balances.map((b) => {
                  const rsv = b.reserved || 0;
                  const avail = b.on_hand - rsv;
                  const raw = physical[b.ref_id] ?? defFor(b);
                  const cnt = raw === "" ? null : Number(raw);
                  const projOnHand = cnt === null ? null : (free ? cnt + rsv : cnt);
                  const projAvail = projOnHand === null ? null : projOnHand - rsv;
                  return (
                    <TableRow key={b.ref_id} data-testid={`opname-form-row-${b.ref_id}`}>
                      <TableCell className="font-mono text-xs">{b.sku || b.item?.name}</TableCell>
                      <TableCell className="text-right">{numberID(b.on_hand)}</TableCell>
                      <TableCell className="text-right text-amber-600">{numberID(rsv)}</TableCell>
                      <TableCell className="text-right">{numberID(avail)}</TableCell>
                      <TableCell className="text-right"><Input type="number" className="h-8 text-right" value={physical[b.ref_id] ?? defFor(b)} onChange={(e) => setPhysical({ ...physical, [b.ref_id]: e.target.value })} data-testid={`opname-form-input-${b.ref_id}`} /></TableCell>
                      <TableCell className="text-right text-xs text-slate-500">{projOnHand === null ? "—" : <span>OH {numberID(projOnHand)}<br/>Av {numberID(projAvail)}</span>}</TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving} data-testid="save-opname-btn" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "..." : "Simpan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function OpnameDetailDialog({ id, whName, onClose, onChanged }) {
  const { can } = useAuth();
  const [d, setD] = useState(null);
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [varOnly, setVarOnly] = useState(false);
  const [page, setPage] = useState(1);
  const [edit, setEdit] = useState(false);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [resumeOpen, setResumeOpen] = useState(false);

  const loadDetail = () => api.get(`/inventory/opnames/${id}`).then((r) => setD(r.data));
  const loadItems = () => api.get(`/inventory/opnames/${id}/items`, { params: { q: q || undefined, variance_only: varOnly, page, page_size: 25 } }).then((r) => setItems(r.data));
  useEffect(() => { loadDetail(); }, [id]);
  useEffect(() => { loadItems(); }, [id, q, varOnly, page]);

  const setQty = (it, v) => setDraft((p) => ({ ...p, [`${it.ref_type}:${it.ref_id}`]: v }));
  const saveDraft = async () => {
    const payload = Object.entries(draft).map(([k, v]) => { const [ref_type, ref_id] = k.split(":"); return { ref_type, ref_id, physical_qty: v === "" ? null : Number(v) }; });
    if (payload.length === 0) { setEdit(false); return; }
    setSaving(true);
    try {
      await api.patch(`/inventory/opnames/${id}`, { expected_rev: d.rev, items: payload });
      toast.success("Perubahan tersimpan"); setDraft({}); setEdit(false);
      await loadDetail(); await loadItems(); onChanged && onChanged();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const a = d?.actions || {};
  const free = (d?.count_basis || "ON_HAND_TOTAL") === "AVAILABLE_FREE";
  const totalPages = items ? Math.max(1, Math.ceil(items.total / items.page_size)) : 1;

  return (
    <Dialog open onOpenChange={(o) => { if (!o && !saving) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="opname-detail-modal">
        {!d ? <Loading /> : (
          <>
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-2">Detail Opname — {d.opname_no} <StatusBadge status={d.status} /></DialogTitle>
              <DialogDescription>{d.warehouse_name} · snapshot {formatDateTime(d.snapshot_at || d.created_at)} · Dasar: {(d.count_basis || "ON_HAND_TOTAL") === "AVAILABLE_FREE" ? "Fisik Stok Bebas" : "Fisik Total Gudang"}</DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 md:grid-cols-6 gap-2 text-xs" data-testid="opname-summary">
              {[["Total", d.summary.total], ["Sudah", d.summary.counted], ["Belum", d.summary.uncounted], ["Selisih +", d.summary.variance_pos], ["Selisih −", d.summary.variance_neg], ["Tanpa Selisih", d.summary.variance_zero]].map(([k, v]) => (
                <div key={k} className="rounded-lg border border-slate-200 p-2"><div className="text-slate-500">{k}</div><div className="text-lg font-semibold">{v}</div></div>
              ))}
            </div>

            <div className="text-xs text-slate-500 space-y-0.5">
              <div>Dibuat: {d.created_by_name} · {formatDateTime(d.created_at)}</div>
              {d.approved_by_name && <div>Disetujui: {d.approved_by_name} · {formatDateTime(d.approved_at)}</div>}
              {d.cancelled_by_name && <div className="text-rose-600">Dibatalkan: {d.cancelled_by_name} · {formatDateTime(d.cancelled_at)} · Alasan: {d.cancel_reason}</div>}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Input placeholder="Cari SKU/nama…" value={q} onChange={(e) => { setPage(1); setQ(e.target.value); }} className="h-8 w-48" data-testid="opname-search" />
              <label className="text-xs flex items-center gap-1"><input type="checkbox" checked={varOnly} onChange={(e) => { setPage(1); setVarOnly(e.target.checked); }} data-testid="opname-filter-variance" /> Hanya berselisih</label>
              <div className="ml-auto flex gap-1">
                {can("inventory.opname") && a.can_edit && !edit && <Button size="sm" variant="outline" onClick={() => setEdit(true)} data-testid="opname-edit-btn">Edit Jumlah</Button>}
                {edit && <><Button size="sm" variant="outline" onClick={() => { setEdit(false); setDraft({}); }}>Batal Edit</Button><Button size="sm" onClick={saveDraft} disabled={saving} data-testid="opname-save-draft" className="bg-emerald-600 hover:bg-emerald-500">{saving ? "…" : "Simpan Draft"}</Button></>}
                {can("inventory.opname") && a.can_cancel && <Button size="sm" variant="outline" onClick={() => setCancelOpen(true)} data-testid="opname-cancel-btn" className="text-rose-600 border-rose-200"><Ban className="mr-1 h-3.5 w-3.5" /> Batalkan</Button>}
              </div>
            </div>
            {!a.can_edit && d.status === "Draft" && <div className="text-xs text-amber-600">{a.edit_reason}</div>}
            {!a.can_cancel && a.cancel_reason && <div className="text-xs text-amber-600" data-testid="opname-cancel-blocked">Tidak dapat dibatalkan: {a.cancel_reason}</div>}
            {d.cancel_lock && d.status === "Disetujui" && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-center justify-between gap-3" data-testid="opname-cancel-inprogress">
                <div className="text-xs text-amber-800">
                  <div className="font-semibold">Pembatalan Sedang Diproses</div>
                  <div>Pembatalan sebelumnya belum selesai (mungkin karena timeout/refresh). Lanjutkan untuk menyelesaikannya dengan aman — kunci operasi dipertahankan otomatis.</div>
                </div>
                {d.is_owner && <Button size="sm" onClick={() => setResumeOpen(true)} data-testid="opname-cancel-resume-btn" className="bg-amber-600 hover:bg-amber-500 shrink-0">Lanjutkan</Button>}
              </div>
            )}

            <div className="rounded-lg border border-slate-200 overflow-x-auto">
              <Table>
                <TableHeader><TableRow>
                  <TableHead>SKU / Produk</TableHead>
                  <TableHead className="text-right">On Hand Sistem</TableHead>
                  <TableHead className="text-right">Reserved</TableHead>
                  <TableHead className="text-right">Available Sistem</TableHead>
                  <TableHead className="text-right w-28">Hitung Fisik</TableHead>
                  <TableHead className="text-right">Selisih</TableHead>
                  <TableHead className="text-right">Estimasi Setelah Setuju</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {!items ? <TableRow><TableCell colSpan={7}><Loading /></TableCell></TableRow> : items.items.length === 0 ? <TableRow><TableCell colSpan={7} className="text-center text-sm text-slate-400 py-4">Tidak ada item</TableCell></TableRow> :
                    items.items.map((it) => {
                      const key = `${it.ref_type}:${it.ref_id}`;
                      const cur = draft[key] !== undefined ? draft[key] : (it.physical_qty ?? "");
                      const rsv = it.reserved_snapshot ?? 0;
                      const base = free && it.system_available != null ? it.system_available : it.system_qty;
                      const vv = cur === "" ? null : Number(cur) - base;
                      const projOnHand = cur === "" || vv === null ? null : it.system_qty + vv;
                      const projAvail = projOnHand === null ? null : projOnHand - rsv;
                      return (
                        <TableRow key={key} data-testid={`opname-item-${it.ref_id}`}>
                          <TableCell className="text-xs"><div className="font-medium">{it.name}</div><div className="font-mono text-slate-400">{it.sku}</div></TableCell>
                          <TableCell className="text-right">{numberID(it.system_qty)}</TableCell>
                          <TableCell className="text-right text-amber-600">{numberID(rsv)}</TableCell>
                          <TableCell className="text-right">{numberID(it.system_available ?? (it.system_qty - rsv))}</TableCell>
                          <TableCell className="text-right">
                            {edit ? <Input type="number" value={cur} onChange={(e) => setQty(it, e.target.value)} className="h-8 text-right" data-testid={`opname-input-${it.ref_id}`} />
                              : (it.physical_qty === null || it.physical_qty === undefined ? <span className="text-slate-400 italic">belum</span> : numberID(it.physical_qty))}
                          </TableCell>
                          <TableCell className={`text-right font-medium ${vv > 0 ? "text-emerald-600" : vv < 0 ? "text-rose-600" : ""}`}>{vv === null ? "—" : (vv > 0 ? "+" : "") + numberID(vv)}</TableCell>
                          <TableCell className="text-right text-xs text-slate-500">{projOnHand === null ? "—" : <span>OH {numberID(projOnHand)}<br/>Av {numberID(projAvail)}</span>}</TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-between text-xs text-slate-500">
              <span>{items?.total ?? 0} item</span>
              <div className="flex items-center gap-2"><Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</Button><span>Hal {page}/{totalPages}</span><Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</Button></div>
            </div>

            <DialogFooter><Button variant="outline" onClick={onClose}>Tutup</Button></DialogFooter>
            {cancelOpen && <CancelOpnameDialog d={d} onClose={() => setCancelOpen(false)} onDone={() => { setCancelOpen(false); loadDetail(); loadItems(); onChanged && onChanged(); }} />}
            {resumeOpen && <CancelOpnameDialog d={d} resume onClose={() => setResumeOpen(false)} onDone={() => { setResumeOpen(false); loadDetail(); loadItems(); onChanged && onChanged(); }} />}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function opnLsKey(id) { return `opn-cancel:${id}`; }
function newIdemKey() {
  try { if (crypto && crypto.randomUUID) return crypto.randomUUID(); } catch { /* noop */ }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function CancelOpnameDialog({ d, onClose, onDone, resume }) {
  const posted = d.status === "Disetujui";
  // Recover a previously-started op (same browser) so a timeout/refresh keeps the SAME key.
  const stored = useMemo(() => { try { return JSON.parse(localStorage.getItem(opnLsKey(d.id)) || "null"); } catch { return null; } }, [d.id]);
  const [reason, setReason] = useState(stored?.reason || "");
  const [confirmNo, setConfirmNo] = useState(resume ? d.opname_no : "");
  const [busy, setBusy] = useState(false);
  // Resume key preference: backend's in-flight op key → our stored key → a fresh key. The user
  // never has to find the key manually; the backend still re-checks Owner permission on every retry.
  const idemKeyRef = useRef((d.cancel_op && d.cancel_op.key) || stored?.key || newIdemKey());
  const submit = async () => {
    setBusy(true);
    const body = { reason: reason.trim(), expected_rev: d.rev, confirm_opname_no: confirmNo.trim() };
    if (posted) {
      body.idempotency_key = idemKeyRef.current;
      // Persist BEFORE the request so a crash/timeout mid-flight can be resumed with the same key.
      try { localStorage.setItem(opnLsKey(d.id), JSON.stringify({ key: idemKeyRef.current, reason: reason.trim() })); } catch { /* noop */ }
    }
    try {
      await api.post(`/inventory/opnames/${d.id}/cancel`, body);
      try { localStorage.removeItem(opnLsKey(d.id)); } catch { /* noop */ }
      toast.success("Opname dibatalkan"); onDone();
    } catch (e) { toast.error(apiError(e)); /* keep key stored so the user can Lanjutkan */ } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="opname-cancel-modal">
        <DialogHeader><DialogTitle className="font-display text-rose-600">{resume ? "Lanjutkan Pembatalan" : "Batalkan"} Opname {d.opname_no}</DialogTitle>
          <DialogDescription>{posted ? "Opname sudah disetujui — pembalik akan membalik seluruh penyesuaian stok asal (khusus Owner)." : "Opname draft — tidak ada perubahan stok."}</DialogDescription></DialogHeader>
        <div className="space-y-2">
          <div><Label className="text-xs">Alasan pembatalan <span className="text-rose-500">*</span></Label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="opname-cancel-reason" /></div>
          <div><Label className="text-xs">Ketik nomor opname untuk konfirmasi</Label><Input value={confirmNo} onChange={(e) => setConfirmNo(e.target.value)} placeholder={d.opname_no} data-testid="opname-cancel-confirm" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose} disabled={busy}>Batal</Button><Button onClick={submit} disabled={busy || reason.trim().length < 3 || confirmNo.trim() !== d.opname_no} className="bg-rose-600 hover:bg-rose-500" data-testid="opname-cancel-submit">{busy ? "…" : (resume ? "Lanjutkan Batalkan" : "Konfirmasi Batalkan")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
